import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/axios';
import {
  deriveFromMargin,
  deriveFromSale,
  formatMoney,
  formatPercent,
  resolveTaxRateValue,
} from '../utils/pricing';
import { formatCurrency } from '../utils/currency';

const buildDefaultState = (taxRates = []) => {
  const zeroRate = taxRates.find((rate) => parseFloat(rate.rate) === 0);
  return {
    name: '',
    sku: '',
    barcode: '',
    categoryId: '',
    taxRateId: zeroRate ? String(zeroRate.id) : '',
    costExclTax: '',
    salePriceExclTax: '0.00',
    salePriceInclTax: '0.00',
    margin: '',
    stock: '',
    active: true,
    isPreorderAvailable: false,
    isPopular: false,
  };
};

const labelClass = 'text-[11px] font-semibold uppercase tracking-wide text-slate-300';
const inputClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-brandGreen/40 focus:ring-2 focus:ring-brandGreen/30';

export default function ProductDrawer({
  show,
  mode = 'edit',
  product = null,
  categories = [],
  taxRates = [],
  onClose,
  onSaved = () => {},
  onRefresh = () => {},
}) {
  const isEditMode = mode === 'edit';
  const [formData, setFormData] = useState(buildDefaultState(taxRates));
  const [message, setMessage] = useState('');
  const [wooPriceDraft, setWooPriceDraft] = useState({ regular: '', sale: '' });
  const [wooSaving, setWooSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const fieldClass = (field) =>
    `${inputClass} ${
      formErrors[field]
        ? 'border-rose-500/60 bg-rose-950/40 text-rose-100 placeholder:text-rose-200/60 focus:border-rose-500/60 focus:ring-rose-500/30'
        : ''
    }`;

  const wooCurrency = product?.woocommerceCurrency || 'GBP';
  const wooCategories = useMemo(
    () => (Array.isArray(product?.woocommerceCategories) ? product.woocommerceCategories : []),
    [product]
  );

  const formatPriceInput = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const numeric = parseFloat(value);
    if (!Number.isFinite(numeric)) return '';
    return formatMoney(numeric, '0.00');
  };

  useEffect(() => {
    if (!show) return;

    if (isEditMode && product) {
      const saleInc = parseFloat(product.priceInclTax ?? product.price ?? 0);
      const derived = deriveFromSale({
        costExclTax: product.costExclTax,
        salePriceInclTax: saleInc,
        taxRate: resolveTaxRateValue(taxRates, product.taxRateId),
      });

      setFormData({
        name: product.name || '',
        sku: product.sku || '',
        barcode: product.barcode || '',
        categoryId: product.categoryId ? String(product.categoryId) : '',
        taxRateId: product.taxRateId ? String(product.taxRateId) : buildDefaultState(taxRates).taxRateId,
        costExclTax:
          product.costExclTax !== undefined && product.costExclTax !== null
            ? String(product.costExclTax)
            : '',
        salePriceInclTax: formatMoney(saleInc, '0.00'),
        salePriceExclTax: formatMoney(derived.salePriceExclTax, '0.00'),
          margin: formatPercent(derived.marginPercent, '0.00'),
          stock: product.stock?.toString() ?? '',
          active: product.active !== false,
          isPreorderAvailable: product.isPreorderAvailable !== undefined ? Boolean(product.isPreorderAvailable) : false,
          isPopular: product.isPopular !== undefined ? Boolean(product.isPopular) : false,
        });
      } else {
        setFormData(buildDefaultState(taxRates));
    }
    setMessage('');
    setFormErrors({});
  }, [show, isEditMode, product, taxRates]);

  useEffect(() => {
    if (!show || !isEditMode || !product?.woocommerceId) {
      setWooPriceDraft({ regular: '', sale: '' });
      return;
    }
    setWooPriceDraft({
      regular: formatPriceInput(product.woocommerceRegularPrice),
      sale: formatPriceInput(product.woocommerceSalePrice),
    });
  }, [show, isEditMode, product]);

  useEffect(() => {
    if (!show) return;
    setFormData((fd) => {
      if (!fd) return fd;
      let next = fd;
      if (!fd.taxRateId) {
        const zeroRate = taxRates.find((rate) => parseFloat(rate.rate) === 0);
        if (zeroRate) {
          next = { ...fd, taxRateId: String(zeroRate.id) };
        }
      }
      const derived = deriveFromSale({
        costExclTax: next.costExclTax,
        salePriceInclTax: next.salePriceInclTax,
        taxRate: resolveTaxRateValue(taxRates, next.taxRateId),
      });
      return {
        ...next,
        salePriceExclTax: formatMoney(derived.salePriceExclTax, '0.00'),
        margin: formatPercent(derived.marginPercent, '0.00'),
      };
    });
  }, [taxRates, show]);

  const taxRateValue = () => resolveTaxRateValue(taxRates, formData?.taxRateId);

  const recalcFromSale = (saleInc, cost = formData.costExclTax) => {
    const derived = deriveFromSale({
      costExclTax: cost,
      salePriceInclTax: saleInc,
      taxRate: taxRateValue(),
    });
    return {
      salePriceExclTax: formatMoney(derived.salePriceExclTax, '0.00'),
      margin: formatPercent(derived.marginPercent, '0.00'),
    };
  };

  const handleSalePriceChange = (value) => {
    const derived = recalcFromSale(value);
    setFormData((fd) => ({
      ...fd,
      salePriceInclTax: value,
      salePriceExclTax: derived.salePriceExclTax,
      margin: derived.margin,
    }));
    setFormErrors((prev) => ({
      ...prev,
      salePriceInclTax: '',
      margin: '',
    }));
  };

  const handleCostChange = (value) => {
    const derived = recalcFromSale(formData.salePriceInclTax, value);
    setFormData((fd) => ({
      ...fd,
      costExclTax: value,
      salePriceExclTax: derived.salePriceExclTax,
      margin: derived.margin,
    }));
    setFormErrors((prev) => ({
      ...prev,
      costExclTax: '',
      margin: '',
    }));
  };

  const handleMarginChange = (value) => {
    if (value.trim() === '') {
      setFormData((fd) => ({ ...fd, margin: value }));
      return;
    }
    const numeric = parseFloat(value);
    if (!Number.isFinite(numeric)) {
      setFormData((fd) => ({ ...fd, margin: value }));
      return;
    }
    if (numeric >= 100) {
      setMessage('Margin must be less than 100%.');
      setFormData((fd) => ({ ...fd, margin: value }));
      return;
    }

    const recalculated = deriveFromMargin({
      costExclTax: formData.costExclTax,
      marginPercent: numeric,
      taxRate: taxRateValue(),
    });

    if (!recalculated) {
      setMessage('Unable to calculate sale price from margin.');
      setFormData((fd) => ({ ...fd, margin: value }));
      return;
    }

    setMessage('');
    setFormData((fd) => ({
      ...fd,
      margin: value,
      salePriceExclTax: formatMoney(recalculated.salePriceExclTax, '0.00'),
      salePriceInclTax: formatMoney(recalculated.salePriceInclTax, '0.00'),
    }));
    setFormErrors((prev) => ({
      ...prev,
      margin: '',
      salePriceInclTax: '',
    }));
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === 'salePriceInclTax') {
      handleSalePriceChange(value);
      return;
    }
    if (name === 'costExclTax') {
      handleCostChange(value);
      return;
    }
    if (name === 'margin') {
      handleMarginChange(value);
      return;
    }

    setFormData((fd) => ({
      ...fd,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Name is required.';
    }
    if (!formData.sku.trim()) {
      errors.sku = 'SKU is required.';
    }

    const salePrice = parseFloat(formData.salePriceInclTax);
    if (!Number.isFinite(salePrice) || salePrice <= 0) {
      errors.salePriceInclTax = 'Sale price must be greater than 0.';
    }

    if (formData.costExclTax !== '' && formData.costExclTax !== null) {
      const cost = parseFloat(formData.costExclTax);
      if (Number.isNaN(cost) || cost < 0) {
        errors.costExclTax = 'Cost must be a positive number or blank.';
      }
    }

    if (formData.stock !== '' && formData.stock !== null) {
      const stockValue = Number(formData.stock);
      if (!Number.isInteger(stockValue) || stockValue < 0) {
        errors.stock = 'Stock must be a whole number greater than or equal to zero.';
      }
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setMessage('Please fix the highlighted fields.');
      return;
    }

    setFormErrors({});

    const payload = {
      ...formData,
      priceExclTax: formData.salePriceExclTax,
      priceInclTax: formData.salePriceInclTax,
      costExclTax: formData.costExclTax,
      stock:
        formData.stock === '' || formData.stock === null
          ? 0
          : Math.max(0, parseInt(formData.stock, 10) || 0),
    };

    try {
      if (isEditMode && product) {
        await api.put(`/products/${product.id}`, payload);
        setMessage('Product updated!');
      } else {
        await api.post('/products', payload);
        setMessage('Product added!');
      }
      onSaved();
      onRefresh();
      if (!isEditMode) {
        setFormData(buildDefaultState(taxRates));
      }
    } catch (err) {
      setMessage(err.response?.data?.error || err.message);
    }
  };

  const adoptWooPrice = async (source) => {
    if (!product) return;
    setMessage('');
    try {
      await api.post(`/products/${product.id}/adopt-price`, { source });
      setMessage('WooCommerce price applied.');
      onRefresh();
    } catch (err) {
      setMessage(err.response?.data?.error || err.message);
    }
  };

  const handleWooPriceFieldChange = (field) => (event) => {
    const { value } = event.target;
    setWooPriceDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleWooPriceSubmit = async () => {
    if (!product) return;
    const regularRaw = wooPriceDraft.regular.trim();
    const saleRaw = wooPriceDraft.sale.trim();

    if (!regularRaw) {
      setMessage('Regular price is required to update WooCommerce.');
      return;
    }

    const regularNumeric = parseFloat(regularRaw);
    if (!Number.isFinite(regularNumeric) || regularNumeric <= 0) {
      setMessage('Enter a valid regular price greater than 0.');
      return;
    }

    let saleNumeric = null;
    if (saleRaw.length > 0) {
      saleNumeric = parseFloat(saleRaw);
      if (!Number.isFinite(saleNumeric) || saleNumeric <= 0) {
        setMessage('Enter a valid sale price greater than 0, or leave blank to remove it.');
        return;
      }
      if (saleNumeric > regularNumeric) {
        setMessage('Sale price cannot be greater than the regular price.');
        return;
      }
    }

    setWooSaving(true);
    setMessage('');

    try {
      const payload = {
        regularPrice: regularNumeric,
        salePrice: saleRaw.length === 0 ? null : saleNumeric,
      };

      await api.post(`/products/${product.id}/woo-price`, payload);
      setMessage('WooCommerce price updated.');
      setWooPriceDraft({
        regular: formatMoney(regularNumeric, '0.00'),
        sale: saleRaw.length === 0 ? '' : formatMoney(saleNumeric, '0.00'),
      });
      onRefresh();
    } catch (err) {
      setMessage(err.response?.data?.error || err.message);
    } finally {
      setWooSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[90] flex justify-end">
      <button
        type="button"
        aria-label="Dismiss product drawer"
        onClick={onClose}
        className="absolute inset-0 h-full w-full bg-slate-950/60 backdrop-blur-sm transition-opacity"
      />
      <div className="relative ml-auto flex h-full w-full max-w-xl flex-col overflow-hidden border-l border-slate-800 bg-slate-900/95 text-slate-100 shadow-[0_30px_80px_-20px_rgba(8,15,26,0.9)]">
        <div className="flex items-start justify-between border-b border-slate-800 px-5 py-4">
          <div className="space-y-1">
            <h3 className="text-base font-semibold uppercase tracking-wide text-slate-100">
              {isEditMode ? 'Edit Product' : 'Add Product'}
            </h3>
            <p className="text-xs text-slate-400">
              {isEditMode
                ? 'Update details, pricing, and WooCommerce metadata.'
                : 'Create a product with cost, sales price, and optional tax rate.'}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close drawer"
            className="rounded-full border border-slate-700 bg-slate-900/60 px-2 py-1 text-sm font-semibold text-slate-300 transition hover:border-slate-500 hover:text-white"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="flex-1 overflow-auto space-y-4 px-5 py-5">
          {message && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
              {message}
            </div>
          )}

          {isEditMode && product?.woocommerceId && (
            <div className="space-y-3 rounded-2xl border border-slate-700 bg-slate-900/70 px-4 py-4">
              <div className="flex items-center justify-between gap-4">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-200">
                  WooCommerce
                </h4>
                <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  {product.woocommerceSyncedAt
                    ? `Synced ${new Date(product.woocommerceSyncedAt).toLocaleString()}`
                    : 'Never synced'}
                </span>
              </div>
            <div className="space-y-1 text-xs text-slate-400">
              <p className="font-medium text-slate-300">ID: {product.woocommerceId}</p>
              {product.woocommerceStatus && <p>Status: {product.woocommerceStatus}</p>}
              {product.woocommerceRegularPrice !== null && (
                <p>
                    Regular:{' '}
                  {formatCurrency(parseFloat(product.woocommerceRegularPrice), wooCurrency)}
                </p>
              )}
              {product.woocommerceSalePrice !== null && (
                <p>
                    Sale:{' '}
                  {formatCurrency(parseFloat(product.woocommerceSalePrice), wooCurrency)}
                </p>
              )}
              {wooCategories.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {wooCategories.map((cat) => (
                    <span
                      key={cat.id}
                      className="rounded-full border border-slate-700 bg-slate-900/60 px-2 py-0.5 text-[10px] text-slate-300"
                    >
                      {cat.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-2 grid grid-cols-1 gap-3 border-t border-slate-800 pt-3">
              <div className="space-y-1">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-300">
                  Regular price
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={wooPriceDraft.regular}
                  onChange={handleWooPriceFieldChange('regular')}
                  className={inputClass}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-300">
                  Sale price (optional)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={wooPriceDraft.sale}
                  onChange={handleWooPriceFieldChange('sale')}
                  className={inputClass}
                  placeholder="Leave blank to remove"
                />
                <p className="text-[10px] text-slate-500">
                  Leave blank to remove the sale price on WooCommerce.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={handleWooPriceSubmit}
                  disabled={wooSaving}
                  className="inline-flex items-center justify-center rounded-full bg-brandGreen px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-white transition hover:bg-brandDark disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {wooSaving ? 'Saving...' : 'Update Woo price'}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="button"
                onClick={() => adoptWooPrice('regular')}
                disabled={product.woocommerceRegularPrice === null}
                  className={`inline-flex items-center justify-center rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-wide transition ${
                    product.woocommerceRegularPrice === null
                      ? 'cursor-not-allowed border border-slate-700/70 bg-slate-800/50 text-slate-500'
                      : 'border border-brandGreen/40 bg-brandGreen/10 text-brandGreen hover:bg-brandGreen/20'
                  }`}
                >
                  Use regular price
                </button>
                <button
                  type="button"
                  onClick={() => adoptWooPrice('sale')}
                  disabled={product.woocommerceSalePrice === null}
                  className={`inline-flex items-center justify-center rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-wide transition ${
                    product.woocommerceSalePrice === null
                      ? 'cursor-not-allowed border border-slate-700/70 bg-slate-800/50 text-slate-500'
                      : 'border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
                  }`}
                >
                  Use sale price
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-3">
            <input
              id="active"
              name="active"
              type="checkbox"
              checked={formData.active}
              onChange={handleChange}
              className="h-4 w-4 rounded border-slate-700 bg-slate-900 text-brandGreen focus:ring-brandGreen"
            />
            <label htmlFor="active" className="text-sm text-slate-300">
              Available on POS
            </label>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="flex items-center gap-2 rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-3 text-sm text-slate-200">
              <input
                id="isPreorderAvailable"
                name="isPreorderAvailable"
                type="checkbox"
                checked={formData.isPreorderAvailable}
                onChange={handleChange}
                className="h-4 w-4 rounded border-slate-700 bg-slate-900 text-brandGreen focus:ring-brandGreen"
              />
              <span>Show on preorder page</span>
            </label>
            <label className="flex items-center gap-2 rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-3 text-sm text-slate-200">
              <input
                id="isPopular"
                name="isPopular"
                type="checkbox"
                checked={formData.isPopular}
                onChange={handleChange}
                className="h-4 w-4 rounded border-slate-700 bg-slate-900 text-brandGreen focus:ring-brandGreen"
              />
              <span>Mark as popular/favourite</span>
            </label>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 pb-6">
            <div className="space-y-1">
              <label htmlFor="name" className={labelClass}>
                Product name
              </label>
              <input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={fieldClass('name')}
                placeholder="e.g. Ground Coffee 1kg"
                required
              />
              {formErrors.name && <p className="text-xs text-rose-300">{formErrors.name}</p>}
            </div>

            <div className="space-y-1">
              <label htmlFor="sku" className={labelClass}>
                SKU
              </label>
              <input
                id="sku"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                className={fieldClass('sku')}
                placeholder="e.g. SKU-12345"
                required
              />
              {formErrors.sku && <p className="text-xs text-rose-300">{formErrors.sku}</p>}
            </div>

            <div className="space-y-1">
              <label htmlFor="categoryId" className={labelClass}>
                Category
              </label>
              <select
                id="categoryId"
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="">Unassigned</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label htmlFor="taxRateId" className={labelClass}>
                Tax rate
              </label>
              <select
                id="taxRateId"
                name="taxRateId"
                value={formData.taxRateId}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="">No tax</option>
                {taxRates.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({(parseFloat(r.rate) * 100).toFixed(2)}%)
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <label htmlFor="costExclTax" className={labelClass}>
                  Cost (excl. tax)
                </label>
                <input
                  id="costExclTax"
                  name="costExclTax"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.costExclTax}
                  onChange={handleChange}
                  className={fieldClass('costExclTax')}
                  placeholder="0.00"
                />
                {formErrors.costExclTax && (
                  <p className="text-xs text-rose-300">{formErrors.costExclTax}</p>
                )}
              </div>

              <div className="space-y-1">
                <label htmlFor="salePriceInclTax" className={labelClass}>
                  Sale price (incl. tax)
                </label>
                <input
                  id="salePriceInclTax"
                  name="salePriceInclTax"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.salePriceInclTax}
                  onChange={handleChange}
                  className={fieldClass('salePriceInclTax')}
                  placeholder="0.00"
                  required
                />
                {formErrors.salePriceInclTax && (
                  <p className="text-xs text-rose-300">{formErrors.salePriceInclTax}</p>
                )}
              </div>

              <div className="space-y-1">
                <label htmlFor="salePriceExclTax" className={labelClass}>
                  Sale price (excl. tax)
                </label>
                <input
                  id="salePriceExclTax"
                  name="salePriceExclTax"
                  value={formData.salePriceExclTax}
                  readOnly
                  className={`${inputClass} bg-slate-900/50`}
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="margin" className={labelClass}>
                  Margin (%)
                </label>
                <input
                  id="margin"
                  name="margin"
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.margin}
                  onChange={handleChange}
                  className={fieldClass('margin')}
                  placeholder="Auto"
                />
                {formErrors.margin && <p className="text-xs text-rose-300">{formErrors.margin}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <label htmlFor="stock" className={labelClass}>
                  Stock on hand
                </label>
                <input
                  id="stock"
                  name="stock"
                  type="number"
                  min="0"
                  value={formData.stock}
                  onChange={handleChange}
                  className={fieldClass('stock')}
                  placeholder="0"
                />
                {formErrors.stock && <p className="text-xs text-rose-300">{formErrors.stock}</p>}
              </div>

              <div className="space-y-1">
                <label htmlFor="barcode" className={labelClass}>
                  Barcode
                </label>
                <input
                  id="barcode"
                  name="barcode"
                  value={formData.barcode}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Optional"
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full rounded-full bg-brandGreen py-2 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-brandDark"
            >
              {isEditMode ? 'Save changes' : 'Create product'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

